import Icon, {
  CheckCircleFilled,
  CloseCircleFilled,
  PlusOutlined,
} from '@ant-design/icons'
import { Button, Form, message, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ReactComponent as NurseIconIconFilled } from '../../assets/Icons_Nurse Fill.svg'
import ActionDropdown, {
  ActionDropdownItemsType,
} from '../../components/ActionDropdown/ActionDropdown'
import { displayMessage } from '../../helpers/Message'
import {
  activateNurse,
  addNurse,
  changeNurseRole,
  deleteNurse,
  errorNurse,
  getNurses,
  selectAllNurses,
  selectErrorInNurse,
  selectNurseByKey,
  selectSuccessInNurse,
  selectNurseLoadingState,
  setNurseForUpdate,
  successNurse,
  updateNurse,
} from '../../state/nurse/nurseReducer'
import { Nurse } from '../../state/nurse/types'
import { NurseViewType } from '../../types/NurseViewTypeIndex'
import Header from '../CyHeader/CyHeader'
import NewNurseView from '../NewNurseView/NewNurseView'
import { generateFullName } from '../PatientsView/PatientsViewStyle'
import { NurseViewContainer } from './NursesViewStyle'

function NursesView() {
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [updateState, setUpdateState] = useState<boolean>(false)
  const nurses = useSelector(selectAllNurses)
  const nurseError = useSelector(selectErrorInNurse)
  const nurseSuccess = useSelector(selectSuccessInNurse)
  const [openNewNurseDrawer, setOpenNewNurseDrawer] = useState(false)
  const [currentKey, setCurrentKey] = useState('')
  const nurseLoadingStatus = useSelector(selectNurseLoadingState)
  const [tableScroll, setTableScroll] = useState<{ x?: number; y?: number }>({ y: 0 });

  useEffect(() => {
    dispatch(getNurses())
  }, [dispatch])

  useEffect(() => {}, [nurses])

  const updateEvent = async (nurseKey: string) => {
    dispatch(
      setNurseForUpdate({
        form: form,
        key: nurseKey,
      })
    )
    setCurrentKey(nurseKey)
    setOpenNewNurseDrawer(true)
    setUpdateState(true)
  }

  const NurseActionDropdown = ({ nurseKey }: { nurseKey: string }) => {
    const currentNurse = useSelector(selectNurseByKey(nurseKey))
    const currentStatus = currentNurse?.isActive === 'N'
    const isSupervisor = currentNurse?.isSupervisor === 'Y'
    const activateStatus = !currentStatus
    const items: ActionDropdownItemsType[] = [
      {
        type: 'toggle',
        onClick: () => {
          dispatch(activateNurse(currentNurse!.nurseKey))
        },
        label: 'Reactivate',
        disabled: activateStatus,
        switchState: activateStatus,
      },
      {
        type: 'confirm',
        label: 'Change Role',
        disabled: !activateStatus,
        onClick: () => {
          dispatch(
            changeNurseRole({
              id: currentNurse!.nurseKey,
              role: isSupervisor ? 'N' : 'Y',
            })
          )
        },
        popConfirmTitle: isSupervisor
          ? 'Are you Sure to change the role to Nurse?'
          : 'Are you Sure to change the role to Chief Nurse?',
      },
      {
        type: 'normal',
        label: 'Update',
        disabled: !activateStatus,
        onClick: () => {
          updateEvent(nurseKey)
        },
      },
      {
        type: 'confirm',
        onClick: () => {
          dispatch(deleteNurse(currentNurse!.nurseKey))
        },
        popConfirmTitle: 'Are you Sure to Delete?',
        label: 'Delete',
        disabled: currentStatus,
      },
    ]

    return <ActionDropdown actionDropdownItems={items} label='...' />
  }

  const StatusIcon = ({ info }: { info: string }) => {
    const statusIcons: Record<string, JSX.Element> = {
      Y: <CheckCircleFilled style={{ color: 'green', fontSize: '30px' }} />,
      N: <CloseCircleFilled style={{ color: 'red', fontSize: '30px' }} />,
    }
    return <>{statusIcons[info]}</>
  }

  const columns: ColumnsType<NurseViewType> = [
    {
      title: 'Nurse ID',
      dataIndex: 'id',
      key: 'id',
      sorter: {
        compare: (a, b) => a.id.localeCompare(b.id),
      },
      width: '120px', 
    },
    {
      title: 'Nurse Name',
      dataIndex: 'name',
      key: 'name',
      sorter: {
        compare: (a, b) => a.name.localeCompare(b.name),
      },
      width: '120px', 
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      sorter: {
        compare: (a, b) => a.phone.localeCompare(b.phone),
      },
      width: '120px', 
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: {
        compare: (a, b) => a.email.localeCompare(b.email),
      },
      width: '120px', 
    },
    {
      title: 'Supervisor',
      dataIndex: 'supervisor',
      key: 'supervisor',
      sorter: {
        compare: (a, b) => a.supervisor.localeCompare(b.supervisor),
      },
      width: '120px', 
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (info: string) => <StatusIcon info={info} />,
      className: 'ant-center',
      sorter: {        
        compare: (a, b) => (a.status || '').localeCompare(b.status || ''),
      },
      defaultSortOrder: 'descend',
      width: '120px', 
    },
    {
      title: 'Action',
      className: 'ant-center',
      dataIndex: 'nurseKey',
      key: 'x',
      render: (nurseKey) => <NurseActionDropdown nurseKey={nurseKey} />,
      width: '120px', 
    },
  ]

  useEffect(() => {
    const updateScroll = () => {        
      setTableScroll(
        window.innerWidth <= 1200
          ? { x: 120 * columns.length, y: 0 } 
          : { y: 0 } 
      );
    };
    
    updateScroll();
    window.addEventListener('resize', updateScroll);
    return () => window.removeEventListener('resize', updateScroll);
  }, [columns.length]);
  const data: NurseViewType[] = nurses.map((item: Nurse, index) => {
    const isSupervisor = (isSupervisor: string) =>
      isSupervisor === 'Y' ? 'Yes' : 'No'
    return {
      key: item.nurseKey,
      id: item.nurseId,
      name: generateFullName(item.title, item.firstName, item.lastName),
      phone: item.phonePrimary,
      secPhone: item.phoneSecondary,
      email: item.emailId,
      supervisor: isSupervisor(item.isSupervisor),
      nurseKey: item.nurseKey,
      status: item.isActive,
    }
  })

  // const subTabItems = [
  //   {
  //     label: 'Live',
  //     key: '1',
  //     children: 'Live',
  //   },
  //   {
  //     label: 'Recorded',
  //     key: '2',
  //     children: 'Recorded',
  //   },
  //   {
  //     label: 'Alerts',
  //     key: '3',
  //     children: 'Alerts',
  //   },
  //   {
  //     label: 'Send SMS',
  //     key: '4',
  //     children: 'Send SMS',
  //   },
  //   {
  //     label: 'Prescription',
  //     key: '5',
  //     children: 'Prescription',
  //   },
  //   {
  //     label: 'Profile',
  //     key: '6',
  //     children: 'Profile',
  //   },
  // ]

  const onNewNurseViewClose = () => {
    setOpenNewNurseDrawer(false)
  }

  const onFinish = async () => {
    const values = form.getFieldsValue()
    let formData = {
      ...values,
    }
    let keys = Object.keys(formData)
    keys.forEach((key) => {
      if (formData[key] === null) {
        formData[key] = ''
      }
    })
    if (updateState) {
      let currentNurseData = { ...formData, nurseKey: currentKey }
      dispatch(updateNurse(currentNurseData))
    } else {
      dispatch(addNurse(formData))
    }
    form.resetFields()
    onNewNurseViewClose()
  }

  useEffect(() => {
    if (nurseError) {
      displayMessage(nurseError, 'error', messageApi)
      dispatch(errorNurse(null))
    }
    if (nurseSuccess) {
      displayMessage(nurseSuccess, 'success', messageApi)
      dispatch(successNurse(null))
    }
  }, [nurseError, nurseSuccess])

  return (
    <>
      <Header
        title={
          <>
            <Icon
              component={NurseIconIconFilled}
              style={{ fontSize: '150%' }}
            />
            <h2>Nurses</h2>
          </>
        }
      />
      <NurseViewContainer>
        <NewNurseView
          onFinish={onFinish}
          updateState={updateState}
          open={openNewNurseDrawer}
          onClose={onNewNurseViewClose}
          form={form}
        />
        {contextHolder}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type='dashed'
            icon={<PlusOutlined />}
            onClick={() => {
              setUpdateState(false)
              form.resetFields()
              if (openNewNurseDrawer) setOpenNewNurseDrawer(false)
              else setOpenNewNurseDrawer(true)
            }}
          >
            Register Nurse
          </Button>
        </div>
        <Table
          columns={columns}
          loading={nurseLoadingStatus}
          dataSource={data}
          className='pageDataTable'
          scroll={tableScroll}
          pagination={{
            position: ['topLeft'],
            pageSizeOptions: [20, 50, 100, 200],
            defaultPageSize: 20,
          }}
        />
      </NurseViewContainer>
    </>
  )
}

export default NursesView
